import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import './ResetPassword.css';

const API = process.env.REACT_APP_BACKEND_URL;

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    hasUpper: false,
    hasLower: false,
    hasNumber: false,
    hasSpecial: false,
    isLong: false
  });

  useEffect(() => {
    if (!token) {
      setError('Invalid reset link. No token provided.');
    }
  }, [token]);

  const checkPasswordStrength = (password) => {
    setPasswordStrength({
      hasUpper: /[A-Z]/.test(password),
      hasLower: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password),
      isLong: password.length >= 8
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    setError('');

    if (name === 'password') {
      checkPasswordStrength(value);
    }
  };

  const validateForm = () => {
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return false;
    }

    if (!passwordStrength.hasUpper || !passwordStrength.hasLower || 
        !passwordStrength.hasNumber || !passwordStrength.hasSpecial) {
      setError('Password must meet all requirements');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      // Scroll to top to show validation errors
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/api/auth/reset-password`, {
        token,
        newPassword: formData.password
      });
      
      setSuccess(true);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      let errorMessage = 'Failed to reset password. The link may have expired.';
      
      if (errorDetail) {
        if (typeof errorDetail === 'string') {
          errorMessage = errorDetail;
        } else if (Array.isArray(errorDetail)) {
          // Pydantic validation errors
          const validationError = errorDetail[0];
          if (validationError?.loc?.includes('newPassword')) {
            errorMessage = 'Password must be at least 8 characters long';
          } else {
            errorMessage = validationError?.msg || errorMessage;
          }
        }
      }
      
      setError(errorMessage);
      setLoading(false);
      // Scroll to top to show the error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  if (success) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-box">
          <div className="success-icon">✓</div>
          <h2>Password Reset Successful!</h2>
          <p>Your password has been successfully reset.</p>
          <p className="redirect-text">Redirecting to login page...</p>
          <button 
            className="btn-primary"
            onClick={() => navigate('/login')}
          >
            Go to Login Now
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-container">
      <div className="reset-password-box">
        <h2>Set New Password</h2>
        <p className="subtitle">
          Please enter your new password below.
        </p>

        {error && (
          <div className="error-banner">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="password">New Password</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="••••••••"
                required
                disabled={loading || !token}
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex="-1"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            
            {formData.password && (
              <div className="password-requirements">
                <div className={`requirement ${passwordStrength.isLong ? 'met' : ''}`}>
                  {passwordStrength.isLong ? '✓' : '○'} At least 8 characters
                </div>
                <div className={`requirement ${passwordStrength.hasUpper ? 'met' : ''}`}>
                  {passwordStrength.hasUpper ? '✓' : '○'} One uppercase letter
                </div>
                <div className={`requirement ${passwordStrength.hasLower ? 'met' : ''}`}>
                  {passwordStrength.hasLower ? '✓' : '○'} One lowercase letter
                </div>
                <div className={`requirement ${passwordStrength.hasNumber ? 'met' : ''}`}>
                  {passwordStrength.hasNumber ? '✓' : '○'} One number
                </div>
                <div className={`requirement ${passwordStrength.hasSpecial ? 'met' : ''}`}>
                  {passwordStrength.hasSpecial ? '✓' : '○'} One special character (!@#$%^&*)
                </div>
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <div className="password-input-wrapper">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="••••••••"
                required
                disabled={loading || !token}
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                tabIndex="-1"
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <button 
            type="submit" 
            className="btn-submit"
            disabled={loading || !token}
          >
            {loading ? 'Resetting Password...' : 'Reset Password'}
          </button>
        </form>

        <div className="footer-links">
          <Link to="/login">← Back to Login</Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
